import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Condition } from '../../hooks/useMultiFilter';
import { getOperatorsForFieldType, inferFieldType } from '../../utils/filterOperators';
import { FilterConditionRow, FilterField } from './FilterConditionRow';

interface FilterPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  fields: FilterField[];
  conditions: Condition[];
  onAddCondition: () => void;
  onRemoveCondition: (index: number) => void;
  onUpdateCondition: (index: number, updates: Partial<Condition>) => void;
  onReorderConditions: (fromIndex: number, toIndex: number) => void;
  onClear: () => void;
}

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  isOpen,
  onClose,
  anchorEl,
  fields,
  conditions,
  onAddCondition,
  onRemoveCondition,
  onUpdateCondition,
  onReorderConditions,
  onClear
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Position popover near anchor
  useEffect(() => {
    if (isOpen && anchorEl && popoverRef.current) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const popover = popoverRef.current;

      popover.style.top = `${anchorRect.bottom + 8}px`;
      popover.style.left = `${anchorRect.left}px`;

      // Trigger animation
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
    }
  }, [isOpen, anchorEl]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorEl &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, anchorEl]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Drag handlers
  const handleDragStart = (index: number) => {
    setDraggingIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggingIndex !== null && draggingIndex !== index) {
      setDropIndicatorIndex(index);
    }
  };

  const handleDrop = (index: number) => {
    if (draggingIndex !== null && draggingIndex !== index) {
      onReorderConditions(draggingIndex, index);
    }
    setDraggingIndex(null);
    setDropIndicatorIndex(null);
  };

  const handleDragEnd = () => {
    setDraggingIndex(null);
    setDropIndicatorIndex(null);
  };

  // Get operators for a field
  const getOperatorsForField = (fieldId: string) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return [];

    const fieldType = inferFieldType(field.name, field.type);
    const operators = getOperatorsForFieldType(fieldType);

    return operators.map(op => ({
      value: op.value,
      label: op.label
    }));
  };

  return (
    <>
      <style>{`
        @keyframes popoverIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div
        ref={popoverRef}
        role="dialog"
        aria-labelledby="filter-popover-title"
        className="fixed bg-white rounded-xl border border-gray-200 flex flex-col overflow-visible"
        style={{
          width: '640px',
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '70vh',
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 10px 15px -3px rgba(0,0,0,0.08), 0 20px 25px -5px rgba(0,0,0,0.05)',
          zIndex: 10000,
          animation: isVisible ? 'popoverIn 150ms ease-out forwards' : 'none',
          opacity: isVisible ? 1 : 0
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 id="filter-popover-title" className="text-[13px] font-semibold text-gray-900">
            Filter
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto overflow-x-visible flex-1">
          {/* Filter conditions */}
          <div className="flex flex-col gap-2">
            {conditions.map((condition, index) => (
              <FilterConditionRow
                key={condition.id}
                condition={condition}
                index={index}
                fields={fields}
                isDragging={draggingIndex === index}
                showDropIndicator={dropIndicatorIndex === index}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
                onUpdate={(updates) => onUpdateCondition(index, updates)}
                onRemove={() => onRemoveCondition(index)}
                getOperatorsForField={getOperatorsForField}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={onAddCondition}
              disabled={conditions.length >= 15}
              className={`text-[12px] font-medium transition-colors ${
                conditions.length >= 15
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              + Add condition
            </button>

            {conditions.length > 0 && (
              <button
                onClick={onClear}
                className="text-[12px] font-medium text-gray-400 hover:text-red-500 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
